<template>
  <!-- Modal action: Open modal if compliance has a registered modal -->
  <WdsButton
    v-if="hasModal"
    :title="unpackedAction.title ?? ''"
    primary
    lg
    :data-test="computedDataTest"
    @click="handleModalAction"
  >
    <WdsStack class="tw-w-full" gap-sm align-center justify-center>
      <div class="tw-whitespace-nowrap">{{ $t(unpackedAction.buttonText ?? 'Open') }}</div>
      <div>
        <WdsIcon :name="WdsIconNameEnum.CHEVRON_RIGHT" :size="16" weight="thin" stroke="white" />
      </div>
    </WdsStack>
  </WdsButton>

  <!-- Tab action: switch to another tab of the hire being viewed, without loading the page again -->
  <WdsButton
    v-else-if="sameHireTabPath"
    :title="unpackedAction.title ?? ''"
    primary
    lg
    :data-test="computedDataTest"
    @click="router.push({ path: sameHireTabPath, query: route.query })"
  >
    <WdsStack class="tw-w-full" gap-sm align-center justify-center>
      <div class="tw-whitespace-nowrap">{{ $t(unpackedAction.buttonText ?? 'Go to') }}</div>
      <div>
        <WdsIcon :name="WdsIconNameEnum.CHEVRON_RIGHT" :size="16" weight="thin" stroke="white" />
      </div>
    </WdsStack>
  </WdsButton>

  <!-- URL action: Navigate to the URL -->
  <WdsButton
    v-else
    :href="urlWithRedirect"
    :title="unpackedAction.title ?? ''"
    primary
    lg
    :data-test="computedDataTest"
  >
    <WdsStack class="tw-w-full" gap-sm align-center justify-center>
      <div class="tw-whitespace-nowrap">{{ $t(unpackedAction.buttonText ?? 'Go to') }}</div>
      <div>
        <WdsIcon :name="WdsIconNameEnum.CHEVRON_RIGHT" :size="16" weight="thin" stroke="white" />
      </div>
    </WdsStack>
  </WdsButton>
</template>

<script lang="ts" setup>
import kebabCase from 'lodash/kebabCase'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { WdsButton, WdsIcon, WdsIconNameEnum, WdsStack } from '@worksome/wds-components'
import { hasComplianceModal } from './modals/complianceModals'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { ComplianceName } from '@/gql/graphql'
import { isHirePageTab } from '@/components/Hire/hirePageTabs'

const ComplianceActionAlertButton = graphql(`
  fragment ComplianceActionAlertButton on ComplianceAction {
    action
    title
    description
    buttonText
    inputIdentifier
  }
`)

const props = defineProps<{
  action: FragmentType<typeof ComplianceActionAlertButton>
  hireId: string
  complianceName: string
  dataTest?: string
}>()

const emit = defineEmits<{
  openModal: [modalId: string]
}>()

const unpackedAction = computed(() => useFragment(ComplianceActionAlertButton, props.action))

const router = useRouter()
const route = useRoute()

const sameHireTabPath = computed((): string | null => {
  const baseUrl = unpackedAction.value.action

  if (!baseUrl || !router || !route?.path.startsWith('/hires/')) return null

  try {
    const url = new URL(baseUrl, window.location.origin)

    // A query or a fragment can trigger something when the page loads, so those links still load the page.
    if (url.origin !== window.location.origin || url.search !== '' || url.hash !== '') return null

    const [, hire, tab] = url.pathname.match(/^\/hires\/([^/]+)\/([^/]+)\/?$/) ?? []

    // Only the hire page's tabs switch in place. Other pages under /hires/{hire}/, such as contract
    // type, load the page.
    return hire === props.hireId && isHirePageTab(tab) ? url.pathname : null
  } catch {
    return null
  }
})

const hasModal = computed(() =>
  hasComplianceModal(props.complianceName as ComplianceName, unpackedAction.value.inputIdentifier),
)

const computedDataTest = computed(
  () => props.dataTest ?? kebabCase(`button-${unpackedAction.value.title ?? ''}`),
)

const urlWithRedirect = computed(() => {
  const baseUrl = unpackedAction.value.action
  if (!baseUrl) return ''

  try {
    const url = new URL(baseUrl, window.location.origin)

    const tab =
      baseUrl.includes('contract-type') || baseUrl.includes('classification')
        ? 'classification'
        : 'overview'

    const redirectUri = laravelNamedRoute(namedRoutes.hires.hire.view, {
      hire: props.hireId,
      tab,
    })

    if (url.protocol === 'http:' || url.protocol === 'https:') {
      url.searchParams.set('redirect_uri', redirectUri)
      return url.toString()
    }

    return baseUrl
  } catch {
    return baseUrl
  }
})

const handleModalAction = () => {
  emit('openModal', props.complianceName)
}
</script>
